import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetFieldTestersWithoutJobs } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import periodLabel from './periodLabel';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

/**
 * Fälttestare som anmält sig tillgängliga men inte hade något uppdrag. Enheten är FT-dagar;
 * avbokade och avbrutna uppdrag räknas som jobb.
 */
const FieldTestersWithoutJobsWidget = () => {
  const { isReady, isDateRangeTooLong, groupingLevel, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetFieldTestersWithoutJobs();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  // Datapunkterna är per (period, hemlän); periodraden summerar länen. Snittet per dygn går
  // inte att summera, så det räknas om från dygnen i perioden.
  const periods = new Map<
    string,
    { date: Date; available: number; without: number; perDay: number }
  >();
  (result?.dataPoints ?? []).forEach((dp) => {
    const key = new Date(dp.date).toISOString();
    const row = periods.get(key) ?? {
      date: new Date(dp.date),
      available: 0,
      without: 0,
      perDay: 0,
    };
    row.available += dp.availableDays;
    row.without += dp.withoutJobDays;
    row.perDay += dp.averageWithoutJobPerDay;
    periods.set(key, row);
  });
  const periodRows = Array.from(periods.values()).sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.FieldTestersWithoutJobs]}
      count={undefined}
      isLoading={getData.isLoading}
      info="Fälttestardagar där FT anmält sig tillgänglig (tider och ingen godkänd frånvaro) men inte hade något uppdrag. Avbokade och avbrutna uppdrag räknas som jobb, kasserade inte. Län = fälttestarens hemlän. Dagar efter idag räknas inte — de är ännu inte planerade. Måttet gäller fälttestarna, inte ärendena: bara länsfiltret och perioden påverkar det, inte ärendetyp, leverantör, kund eller företagsundantag."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={!result || result.availableDays === 0}
      >
        {result && !result.fieldTesterRoleResolved ? (
          <p>Rollen Fälttestare hittades inte — måttet går inte att räkna.</p>
        ) : (
          <>
            <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
              <strong>{pct(result?.withoutJobPercentage)}</strong> av
              tillgängliga FT-dagar utan jobb · i snitt{' '}
              {nf(result?.averageWithoutJobPerDay ?? 0, 1)} FT per dygn ·{' '}
              {nf(result?.fieldTestersWithoutJob ?? 0)} olika FT
            </p>

            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Period</th>
                  <th style={thNumStyle}>Tillgängliga dagar</th>
                  <th style={thNumStyle}>Utan jobb</th>
                  <th style={thNumStyle}>Andel</th>
                  <th style={thNumStyle}>Snitt per dygn</th>
                </tr>
              </thead>
              <tbody>
                {periodRows.map((row) => (
                  <tr
                    key={row.date.toISOString()}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={tdStyle}>
                      {periodLabel(row.date, groupingLevel)}
                    </td>
                    <td style={tdNumStyle}>{nf(row.available)}</td>
                    <td style={tdNumStyle}>{nf(row.without)}</td>
                    <td style={tdNumStyle}>
                      {pct(
                        row.available > 0
                          ? (row.without / row.available) * 100
                          : null
                      )}
                    </td>
                    <td style={tdNumStyle}>{nf(row.perDay, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <table style={{ ...tableStyle, marginTop: '16px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Hemlän</th>
                  <th style={thNumStyle}>Tillgängliga dagar</th>
                  <th style={thNumStyle}>Utan jobb</th>
                  <th style={thNumStyle}>Andel</th>
                  <th style={thNumStyle}>Snitt per dygn</th>
                  <th style={thNumStyle}>Olika FT</th>
                </tr>
              </thead>
              <tbody>
                {(result?.byCounty ?? []).map((row) => (
                  <tr
                    key={row.countyName}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={tdStyle}>{row.countyName}</td>
                    <td style={tdNumStyle}>{nf(row.availableDays)}</td>
                    <td style={tdNumStyle}>{nf(row.withoutJobDays)}</td>
                    <td style={tdNumStyle}>{pct(row.withoutJobPercentage)}</td>
                    <td style={tdNumStyle}>
                      {nf(row.averageWithoutJobPerDay, 1)}
                    </td>
                    <td style={tdNumStyle}>{nf(row.fieldTestersWithoutJob)}</td>
                  </tr>
                ))}
                <tr style={totalRowStyle}>
                  <td style={tdStyle}>Totalt</td>
                  <td style={tdNumStyle}>{nf(result?.availableDays ?? 0)}</td>
                  <td style={tdNumStyle}>{nf(result?.withoutJobDays ?? 0)}</td>
                  <td style={tdNumStyle}>
                    {pct(result?.withoutJobPercentage)}
                  </td>
                  <td style={tdNumStyle}>
                    {nf(result?.averageWithoutJobPerDay ?? 0, 1)}
                  </td>
                  <td style={tdNumStyle}>
                    {nf(result?.fieldTestersWithoutJob ?? 0)}
                  </td>
                </tr>
              </tbody>
            </table>

            <ControlFigures
              items={[
                result?.fieldTestersWithoutHomeCounty
                  ? `${nf(result.fieldTestersWithoutHomeCounty)} tillgängliga FT saknar hemlän`
                  : null,
              ]}
            />
          </>
        )}
      </WidgetState>
    </Widget>
  );
};

export default FieldTestersWithoutJobsWidget;
